import { useRecoilTask, type RecoilTaskInterface } from 'recoil-toolkit';

import { userState } from '../../../state/user';
import type { EventSettings } from '../../../types';
import { eventSettingService } from '../api/eventSettings';

type EventSettingsGridType = 'incident' | 'drs' | 'pmp';
type EventSettingsGridKey = 'incidentGridSettings' | 'drsGridSettings' | 'pmpGridSettings';
type GridFontSize = EventSettings['incidentGridSettings']['fontSize'];

const gridKeyMap: Record<EventSettingsGridType, EventSettingsGridKey> = {
  incident: 'incidentGridSettings',
  drs: 'drsGridSettings',
  pmp: 'pmpGridSettings',
};

const loaderStackMap: Record<EventSettingsGridType, string> = {
  incident: 'incidentGridFontSizeUpdate',
  drs: 'dailyTaskGridFontSizeUpdate',
  pmp: 'projectTaskGridFontSizeUpdate',
};

const setFontSizeTask =
  (gridType: EventSettingsGridType) =>
  ({ getLoadable, set }: RecoilTaskInterface) =>
  async (fontSize: GridFontSize) => {
    const currentUser = getLoadable(userState).getValue();
    const currentEventSetting = currentUser.eventSettings.find(
      (eventSetting) => eventSetting.event.id === currentUser.currentEvent.id,
    ) as EventSettings;
    const gridKey = gridKeyMap[gridType];
    const currentGridSettings = currentEventSetting[gridKey];

    if (currentGridSettings.fontSize === fontSize) {
      return;
    }

    const { data } = await eventSettingService.update({
      id: currentEventSetting.id,
      [gridKey]: {
        ...currentGridSettings,
        fontSize,
      },
    });

    set(userState, (state) => ({
      ...state,
      eventSettings: state.eventSettings.map((eventSetting) => {
        if (eventSetting.id === data.data.id) {
          return {
            ...eventSetting,
            ...data.data,
          };
        }

        return eventSetting;
      }),
    }));
  };

export const useSetFontSize = (gridType: EventSettingsGridType) =>
  useRecoilTask(setFontSizeTask(gridType), [gridType], {
    loaderStack: loaderStackMap[gridType],
    errorStack: true,
  });
